import React, { useEffect, useRef, useState } from 'react';
import { Box, Chip, Collapse, Grid, IconButton, Typography } from '@mui/material';
import { CheckCircle, ContentCopy, ErrorOutline, ExpandLess, ExpandMore } from '@mui/icons-material';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import {
  AccountData,
  AuditLogService,
  AuditType,
  OrderData,
  OrderService,
  PassPaymentAuditInfoData,
  RefundablePaymentData,
} from '../../api';
import {
  AuditLogChangeDetails,
  AuditLogData,
  PunchAdjustmentAuditDetails,
  RefundAuditDetails,
  isPassAuditLog,
} from '../../auditLog';
import { getDateFormat, getTimeFormat } from '../../common/getDateAndTimeFormat';
import { getCurrencySymbol } from '../../utils/utils';
import TimeUtils from '../../utils/TimeUtils';
import { RotatingLoader } from '../../common/ui/RotatingLoader';
import { useLanguage } from '../../common/GeneralUtils';
import { getOrderPage } from '../../utils/AccountUtils';
import { RefundIconButton } from '../../refunds/RefundIconButton';
import { useRefundAmountFormatter } from '../../refunds/refundFormat';

type PassActivityLogProps = {
  passId: string;
  expanded: boolean;
  passUpdatedAt: string;
  usersById: Map<string, string>;
  account: AccountData;
};

const Dot = () => (
  <Typography variant="caption" color="text.secondary" component="span">
    ·
  </Typography>
);

export const PassPaymentAuditDetails = ({
  payment,
  account,
  refundItem,
  onRefund,
}: {
  payment: PassPaymentAuditInfoData;
  account: AccountData;
  refundItem?: RefundablePaymentData;
  onRefund?: (item: RefundablePaymentData) => void;
}) => {
  const { t } = useTranslation();
  const dateFormat = getDateFormat(account).longDateFormat;
  const currencySymbol = getCurrencySymbol(account);
  const formatRefundAmount = useRefundAmountFormatter();
  const succeeded = payment.succeeded !== false;
  const refundedAmount = formatRefundAmount(refundItem?.refunded_amount ?? 0);

  return (
    <Box
      sx={{
        mt: 0.5,
        p: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '6px',
        backgroundColor: succeeded ? 'primary.light' : 'error.light',
        maxWidth: 480,
      }}>
      <Grid container alignItems="center" gap={1} mb={0.5}>
        {succeeded ? (
          <CheckCircle fontSize="small" sx={{ color: 'success.main' }} />
        ) : (
          <ErrorOutline fontSize="small" sx={{ color: 'error.main' }} />
        )}
        <Typography variant="body2" fontWeight={600}>
          {succeeded ? t('auditlog.pass_payment.succeeded') : t('auditlog.pass_payment.failed')}
        </Typography>
        {payment.price != null && (
          <Typography variant="body2">
            {payment.price}
            {currencySymbol}
          </Typography>
        )}
        {!!refundItem?.refunded_amount && !!refundedAmount && (
          <Chip size="small" variant="outlined" label={t('refunds.row.refundedChip', { amount: refundedAmount })} />
        )}
        {onRefund && (
          <>
            <Grid flex={1} />
            <RefundIconButton item={refundItem} onRefund={onRefund} />
          </>
        )}
      </Grid>
      {payment.provider_transaction_id && (
        <Grid container alignItems="center" gap={0.5}>
          <Typography variant="caption" color="text.secondary">
            {t('auditlog.pass_payment.transaction_id')}:
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontFamily: 'monospace',
              cursor: 'copy',
              maxWidth: 240,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            onClick={() => navigator.clipboard.writeText(payment.provider_transaction_id!)}
            title={`${payment.provider_transaction_id} (click to copy)`}>
            {payment.provider_transaction_id}
          </Typography>
          <ContentCopy sx={{ fontSize: 12, color: 'text.secondary' }} />
        </Grid>
      )}
      {payment.cycle_start && (
        <Typography variant="caption" color="text.secondary" display="block">
          {t('auditlog.pass_payment.cycle_start')}:{' '}
          {TimeUtils.getLocalTime(payment.cycle_start, account.timezone).format(dateFormat)}
        </Typography>
      )}
      {!succeeded && payment.failure_reason && (
        <Typography variant="caption" color="error.main" display="block">
          {t('auditlog.pass_payment.failure_reason')}: {payment.failure_reason}
        </Typography>
      )}
    </Box>
  );
};

const PassOrderAuditDetails = ({ orderId, account }: { orderId: string; account: AccountData }) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const [order, setOrder] = useState<OrderData | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const dateFormat = getDateFormat(account).longDateFormat;
  const timeFormat = getTimeFormat(account);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setFailed(false);
    void (async () => {
      try {
        const fetched = await OrderService.getOrder(orderId);
        if (!cancelled) setOrder(fetched);
      } catch (error) {
        if (!cancelled) {
          console.error('Error fetching order for audit log:', error);
          setFailed(true);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  if (isLoading) {
    return (
      <Box py={0.5}>
        <RotatingLoader width="24px" />
      </Box>
    );
  }
  if (failed || !order) {
    return (
      <Typography variant="caption" color="text.secondary">
        {t('auditlog.order_details.load_error')}
      </Typography>
    );
  }

  const orderPage = getOrderPage(account, order.order_page_id);
  const pageName = orderPage?.label?.[language] || orderPage?.label?.en || '';
  const dateLabel = order.date ? moment(order.date).format(dateFormat) : '';
  const timeLabel = order.time != null ? moment.unix(order.time).tz('utc').format(timeFormat) : '';
  const dateTime = [dateLabel, timeLabel].filter(Boolean).join(' ');
  const customer = `${order.firstname} ${order.lastname}`.trim();

  return (
    <Box
      sx={{
        mt: 0.5,
        p: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '6px',
        maxWidth: 480,
      }}>
      <Grid container flexDirection="column" gap={0.25}>
        <Typography variant="body2" fontWeight={600}>
          #{order.order_number}
          {pageName ? ` · ${pageName}` : ''}
        </Typography>
        {dateTime && (
          <Typography variant="caption" color="text.secondary">
            {dateTime}
          </Typography>
        )}
        {customer && <Typography variant="caption">{customer}</Typography>}
      </Grid>
    </Box>
  );
};

type PassActivityLogRowProps = {
  log: AuditLogData;
  userName: string;
  dateTimeFormat: string;
  account: AccountData;
};

const PassActivityLogRow = ({ log, userName, dateTimeFormat, account }: PassActivityLogRowProps) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const changes = isPassAuditLog(log) ? log.changes ?? [] : [];
  const hasChanges = changes.length > 0;
  const payment = isPassAuditLog(log) ? log.payment : undefined;
  const orderId = isPassAuditLog(log) ? log.order_id : undefined;
  const isPunchAdjustment = log.audit_type === AuditType.PASS_PUNCHES_ADJUSTED;
  const canExpand = !isPunchAdjustment && (hasChanges || !!payment || !!orderId || !!log.refund);

  return (
    <Box
      sx={{
        py: 1,
        borderBottom: '1px solid',
        borderColor: 'mb.lineSoft',
        '&:last-child': { borderBottom: 'none' },
      }}>
      <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
        <Typography variant="body2" color="text.secondary">
          {moment(log.created_at).format(dateTimeFormat)}
        </Typography>
        <Dot />
        <Typography variant="body2" fontWeight={600}>
          {t(`auditlog.audit_types.${log.audit_type}`)}
        </Typography>
        <Dot />
        <Typography variant="body2">{userName}</Typography>
        {hasChanges && !isPunchAdjustment && (
          <>
            <Dot />
            <Chip size="small" label={changes.length} variant="outlined" />
          </>
        )}
        {payment && (
          <>
            <Dot />
            <Chip
              size="small"
              label={t('auditlog.pass_payment.chip_label')}
              color={payment.succeeded !== false ? 'success' : 'error'}
              variant="outlined"
            />
          </>
        )}
        {canExpand && (
          <IconButton
            size="small"
            onClick={() => setIsExpanded((v) => !v)}
            sx={{ marginInlineStart: 'auto' }}
            aria-label={isExpanded ? t('auditlog.change_details.collapse') : t('auditlog.change_details.expand')}>
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        )}
      </Box>
      {isPunchAdjustment && isPassAuditLog(log) && <PunchAdjustmentAuditDetails log={log} />}
      {canExpand && (
        <Collapse in={isExpanded} unmountOnExit>
          <Box sx={{ mt: 0.5, paddingInlineStart: 1 }}>
            {hasChanges && <AuditLogChangeDetails changes={changes} expanded hideChangesNumber />}
            {payment && <PassPaymentAuditDetails payment={payment} account={account} />}
            {log.refund && <RefundAuditDetails refund={log.refund} auditType={log.audit_type} />}
            {orderId && <PassOrderAuditDetails orderId={orderId} account={account} />}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

export const PassActivityLog = ({ passId, expanded, passUpdatedAt, usersById, account }: PassActivityLogProps) => {
  const { t } = useTranslation();
  const [auditLogs, setAuditLogs] = useState<AuditLogData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const lastFetchedKey = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!expanded || !passId) return undefined;
    const fetchKey = `${passId}:${passUpdatedAt}`;
    if (lastFetchedKey.current === fetchKey) return undefined;

    let cancelled = false;
    setIsLoading(true);
    void (async () => {
      try {
        const response = await AuditLogService.getAuditLogs(
          undefined,
          undefined,
          passId,
          undefined,
          undefined,
          undefined,
          1,
          1000,
        );
        if (cancelled) return;
        setAuditLogs(response as AuditLogData[]);
        lastFetchedKey.current = fetchKey;
      } catch (error) {
        if (!cancelled) console.error('Error fetching pass audit logs:', error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [expanded, passId, passUpdatedAt]);

  const dateTimeFormat = `${getDateFormat(account).longDateFormat} ${getTimeFormat(account)}`;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={2}>
        <RotatingLoader width="40px" />
      </Box>
    );
  }

  if (auditLogs.length === 0) {
    return (
      <Box py={2} display="flex" justifyContent="center">
        <Typography variant="body2" color="text.secondary">
          {t('auditlog.no_pass_activity_found')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {auditLogs.map((log) => {
        const isSystemAction =
          log.audit_type === AuditType.PASS_RENEWED || log.audit_type === AuditType.PASS_RENEWAL_FAILED;
        const userName: string = log.user_id
          ? log.user_id === 'system'
            ? t('auditlog.system')
            : usersById.get(log.user_id) ?? t('auditlog.mbuser')
          : isSystemAction
          ? t('auditlog.system')
          : t('auditlog.customer');
        return (
          <PassActivityLogRow
            key={`${log.audit_type}-${log.created_at}`}
            log={log}
            userName={userName}
            dateTimeFormat={dateTimeFormat}
            account={account}
          />
        );
      })}
    </Box>
  );
};

export default PassActivityLog;
